/**
 * Tour step definitions — all step arrays and step-getter functions.
 * Pure data: no React, no DOM, no side effects.
 */
import type { TourStep, FeaturePageTour } from "./tourTypes";

// ─── Sidebar / Navigation Tour Steps ────────────────────────────────────────

export const getDesktopSteps = (isAdmin: boolean): TourStep[] => {
  const baseSteps: TourStep[] = [
    {
      element: '[data-tour="dashboard"]',
      popover: {
        title: "Dashboard",
        description: isAdmin
          ? "Admin dashboard with analytics and user stats."
          : "Your main dashboard with quick actions.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="cv-builder"]',
      popover: {
        title: "Resume Builder",
        description: "Create ATS-friendly resumes using guided templates.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="my-resumes"]',
      popover: {
        title: "My Resumes",
        description: "Open, update, and manage your saved resumes.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="templates"]',
      popover: {
        title: "Templates",
        description: "Browse styles by role and industry.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="blog"]',
      popover: {
        title: "Blog",
        description: "Read practical career tips and resume guidance.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="ai-assistant"]',
      popover: {
        title: "AI Assistant",
        description: "Ask AI for resume writing and career support.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="profile"]',
      popover: {
        title: "Profile",
        description: "Manage your account and guidance settings.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="feedback"]',
      popover: {
        title: "Feedback",
        description: "Share ratings and suggestions anytime.",
        side: "right",
        align: "start",
      },
    },
  ];

  if (isAdmin) {
    baseSteps.splice(1, 0, {
      element: '[data-tour="create-post"]',
      popover: {
        title: "Create Post",
        description: "Publish blog content for users.",
        side: "right",
        align: "start",
      },
    });
  }

  return baseSteps;
};

export const getMobileSteps = (isAdmin: boolean): TourStep[] => [
  {
    element: '[data-tour-mobile="home"]',
    popover: {
      title: isAdmin ? "Dashboard" : "Home",
      description: "Quick overview and shortcuts.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="builder"]',
    popover: {
      title: "Resume Builder",
      description: "Create and update resumes from here.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="resumes"]',
    popover: {
      title: "My Resumes",
      description: "Manage your saved resumes.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="templates"]',
    popover: {
      title: "Templates",
      description: "Explore layout options quickly.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="blog"]',
    popover: {
      title: "Blogs",
      description: "Read resume and career posts.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="feedback"]',
    popover: {
      title: "Feedback",
      description: "Share your experience.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour-mobile="ai"]',
    popover: {
      title: "AI",
      description: "Ask AI for writing and interview help.",
      side: "top",
      align: "center",
    },
  },
];

// ─── Journey / Builder Flow Steps ───────────────────────────────────────────

export const getJourneyIntroSteps = (mobile: boolean): TourStep[] =>
  mobile
    ? [
        {
          element: '[data-tour-mobile="home"]',
          popover: {
            title: "Start Here",
            description: "We will guide real resume creation first.",
            side: "top",
            align: "center",
          },
        },
        {
          element: '[data-tour-mobile="builder"]',
          popover: {
            title: "Open Resume Builder",
            description: "Tap Builder, then choose Create New or Upload.",
            side: "top",
            align: "center",
          },
        },
      ]
    : [
        {
          element: '[data-tour="dashboard"]',
          popover: {
            title: "Home",
            description: "We begin with a real resume creation flow.",
            side: "right",
            align: "start",
          },
        },
        {
          element: '[data-tour="cv-builder"]',
          popover: {
            title: "Open Resume Builder",
            description: "Choose Create New or Upload, then continue.",
            side: "right",
            align: "start",
          },
        },
      ];

export const getBuilderChoiceInstructionSteps = (): TourStep[] => [
  {
    element: '[data-tour="builder-selector-title"]',
    popover: {
      title: "Choose One Option",
      description:
        "Please select either Create New Resume or Upload Your Existing CV. The flow will continue based on your real choice.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: '[data-tour="builder-create-option"]',
    popover: {
      title: "Create New Resume",
      description:
        "This path opens the design gallery — pick a template and the editor opens.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: '[data-tour="builder-upload-option"]',
    popover: {
      title: "Upload Existing CV",
      description:
        "This path asks you to upload your current CV and continue after extraction.",
      side: "bottom",
      align: "center",
    },
  },
];

export const getTemplateHintSteps = (): TourStep[] => [
  {
    element: '[data-tour="builder-template-card"]',
    popover: {
      title: "Choose Template",
      description: "Select your preferred template now.",
      side: "bottom",
      align: "center",
    },
  },
];

export const getUploadPathInstructionSteps = (): TourStep[] => [
  {
    element: '[data-tour="builder-upload-dropzone"]',
    popover: {
      title: "Upload Existing Resume",
      description:
        "Upload your current CV file here. We will wait until extraction finishes and editor opens.",
      side: "bottom",
      align: "center",
    },
  },
];

export const getEditorInstructionSteps = (mobile: boolean): TourStep[] => [
  {
    element: '[data-tour="builder-editor-title"]',
    popover: {
      title: "Editor",
      description: "Now you are in edit-and-preview mode.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-tour="builder-full-name"]',
    popover: {
      title: "Enter Your Name",
      description: "Type your name in the Full Name field.",
      side: mobile ? "bottom" : "right",
      align: "start",
    },
  },
  {
    element: mobile
      ? '[data-tour="builder-save-btn-mobile"]'
      : '[data-tour="builder-header-actions"]',
    popover: {
      title: "Editing Toolbar",
      description: mobile
        ? "Save, Preview, and PDF buttons are right here on the bottom bar."
        : "Your resume, design and layout pickers live up here, along with Save, Preview and PDF.",
      side: mobile ? "top" : "bottom",
      align: "center",
    },
  },
  // Desktop-only: the header has room to call out each action separately
  ...(!mobile
    ? ([
        {
          element: '[data-tour="builder-save-btn"]',
          popover: {
            title: "Save Your Resume",
            description:
              "Click Save (or Update for existing) to store all your changes.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tour="builder-preview-btn"]',
          popover: {
            title: "Preview as PDF",
            description: "Open a full PDF preview of your resume.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tour="builder-download-btn"]',
          popover: {
            title: "Download PDF",
            description: "Export and download your resume as a PDF file.",
            side: "bottom",
            align: "center",
          },
        },
      ] as TourStep[])
    : []),
  {
    element: mobile
      ? '[data-tour="builder-edit-tab-mobile"]'
      : '[data-tour="builder-form-panel"]',
    popover: {
      title: "Edit Sections",
      description: "Update experience, education, projects, and skills.",
      side: mobile ? "top" : "right",
      align: mobile ? "center" : "start",
    },
  },
  {
    element: mobile
      ? '[data-tour="builder-preview-tab-mobile"]'
      : '[data-tour="builder-preview-panel"]',
    popover: {
      title: "Preview",
      description: "Use preview to verify final layout.",
      side: "top",
      align: "center",
    },
  },
];

// ─── Rating Steps ────────────────────────────────────────────────────────────

export const getRatingLauncherStep = (): TourStep[] => [
  {
    element: '[data-tour="rating-launcher"]',
    popover: {
      title: "Real Rating",
      description: "Open the rating prompt and submit a real 5-star rating.",
      side: "left",
      align: "center",
    },
  },
];

export const getRatingPopupSteps = (): TourStep[] => [
  {
    element: '[data-tour="rating-stars"]',
    popover: {
      title: "Pick Stars",
      description: "Select your star rating first.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-tour="rating-submit"]',
    popover: {
      title: "Submit Rating",
      description: "Submit to complete the rating step.",
      side: "top",
      align: "center",
    },
  },
];

// ─── Feature Page Tours ──────────────────────────────────────────────────────

const showProfileSection = (section: "profile" | "security" | "legal") => {
  window.dispatchEvent(
    new CustomEvent("tour:profile-show-section", { detail: { section } }),
  );
};

export const getFeaturePageTours = (): FeaturePageTour[] => [
  {
    path: "/home",
    waitFor: '[data-tour="feature-home-actions"]',
    steps: [
      {
        element: '[data-tour="feature-home-actions"]',
        popover: {
          title: "Home Features",
          description: "Quick actions to create resume or open saved resumes.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-tour="feature-home-recent"]',
        popover: {
          title: "Recent Resumes",
          description: "Continue quickly from your latest resume list.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },
  {
    path: "/my-resumes",
    waitFor: '[data-tour="feature-resumes-list"]',
    steps: [
      {
        element: '[data-tour="feature-resumes-header"]',
        popover: {
          title: "My Resumes",
          description: "View all saved resumes and creation stats.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-tour="feature-resumes-create"]',
        popover: {
          title: "Create New",
          description: "Start a new resume anytime from here.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },
  {
    path: "/templates",
    waitFor: '[data-tour="feature-templates-results"]',
    steps: [
      {
        element: '[data-tour="feature-templates-results"]',
        popover: {
          title: "Design Gallery",
          description:
            "Browse every layout, preview it at full size, and start a resume from the one you like.",
          side: "top",
          align: "center",
        },
      },
      {
        element: '[data-tour="feature-templates-how"]',
        popover: {
          title: "How It Works",
          description:
            "Pick a design, fill in your details, then download — your content stays the same across designs.",
          side: "top",
          align: "center",
        },
      },
    ],
  },
  {
    path: "/blog",
    waitFor: '[data-tour="feature-blog-filters"]',
    steps: [
      {
        element: '[data-tour="feature-blog-search"]',
        popover: {
          title: "Blog Search",
          description: "Search and filter blogs by reading time and sorting.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-tour="feature-blog-read"]',
        popover: {
          title: "Read, Like, Comment",
          description:
            "Open a post to read details, like content, and comment.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },
  {
    path: "/ai-assistant",
    waitFor: '[data-tour="feature-ai-input-wrap"]',
    // One chat for CV building and career advice. The chats list only shows
    // on desktop, so its step is added only when it's actually on screen.
    steps: () => {
      const chatsVisible = Boolean(
        document
          .querySelector('[data-tour="feature-ai-chats"]')
          ?.getClientRects().length,
      );
      return [
        {
          element: '[data-tour="feature-ai-input-wrap"]',
          popover: {
            title: "AI Assistant",
            description:
              "Chat like you would with any assistant — paste your details or attach your CV to build or review it, check its ATS score, or ask for career advice.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tour="feature-ai-prompts"]',
          popover: {
            title: "Quick Starts",
            description:
              "One click to review your CV, check its ATS score, start a CV from scratch, or get career advice.",
            side: "top",
            align: "center",
          },
        },
        ...(chatsVisible
          ? ([
              {
                element: '[data-tour="feature-ai-chats"]',
                popover: {
                  title: "Your Chats",
                  description:
                    "Every conversation is saved here, named by topic — open one to pick up where you left off.",
                  side: "right",
                  align: "start",
                },
              },
            ] as TourStep[])
          : []),
      ];
    },
  },
  {
    path: "/profile",
    waitFor: '[data-tour="feature-profile-edit"]',
    steps: [
      {
        element: '[data-tour="feature-profile-edit"]',
        popover: {
          title: "Profile Edit",
          description: "Update your profile name and account details here.",
          side: "bottom",
          align: "center",
          // On desktop the Profile page shows one section at a time and "Tour
          // Controls" lives under Security & Help. Switch there on Next, then
          // let React paint before driver.js measures the target — doing it in
          // onDeselected was too late, the next step was already positioned.
          onNextClick: (_element, _step, { driver }) => {
            showProfileSection("security");
            window.setTimeout(() => driver.moveNext(), 150);
          },
        },
      },
      {
        element: '[data-tour="feature-profile-tour"]',
        popover: {
          title: "Tour Controls",
          description:
            "You can start or restart guidance from Profile anytime.",
          side: "left",
          align: "center",
          onPrevClick: (_element, _step, { driver }) => {
            showProfileSection("profile");
            window.setTimeout(() => driver.movePrevious(), 150);
          },
        },
      },
    ],
  },
  {
    path: "/feedback",
    waitFor: '[data-tour="feature-feedback-stars"]',
    steps: [
      {
        element: '[data-tour="feature-feedback-quick"]',
        popover: {
          title: "Quick Rating",
          description: "Choose quick mode for a simple star-only rating.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-tour="feature-feedback-submit"]',
        popover: {
          title: "Submit Feedback",
          description: "Submit quick or detailed feedback from this page.",
          side: "top",
          align: "center",
        },
      },
    ],
  },
];
